import moment from 'moment'
import {
    canViewProjectDetailsExtraInfo,
    LayoutActivityType,
    SmartlyActionItems,
    SmartlyActionSubtypes,
    SmartlyActionTypes,
} from '@Techedge/digitalportal-shared'
import { ProjectApiCreateActivityBody, ProjectApiStatus } from '../models/projects-service'
import { StoreProps } from '../models/store'
import { formatDate, formatDateLocale } from './commonFunctions'
import { NewActivitySectionType } from '../models/new-activity-section-type'

const isTaskCompleted = (questions) => {
    if (!Array.isArray(questions)) questions = [questions]
    return (
        !!questions &&
        questions.length > 0 &&
        questions.filter((question) => !!question.answer).length === questions.length
    )
}
const setTaskQuestionStatus = (questions) => {
    if (!Array.isArray(questions)) questions = [questions]
    return !!questions
        ? questions.map((question) => ({
              title: question.questionTitle,
              answer:
                  question.questionType === 'date'
                      ? formatDateLocale(formatDate(Number(question.answer)))
                      : question.answer,
              type: question.questionType,
              attachments: question.answerAttachments,
          }))
        : []
}

export const getStoreDetailsTaskData = (taskStore, completedMsg, notCompletedMsg, intl) => {
    return taskStore[0].jobStore.map((task, index) => ({
        task: task.title,
        role:
            task.assignment && task.assignment.length
                ? intl.formatMessage({
                      id: task.assignment[0],
                  })
                : null,
        start_date: formatDateLocale(formatDate(task.startDate)),
        end_date: formatDateLocale(formatDate(task.endDate)),
        priority: intl.formatMessage({
            id: `modals.new.activity.section.scheduling.priority.${task?.priority}`,
        }),
        description: task.details,
        completion: !!isTaskCompleted(task.questions) ? completedMsg : notCompletedMsg,
        questions: setTaskQuestionStatus(task.questions),
        attachments: task.attachments,
        id: index,
    }))
}

export const getStoreDetailsQuestionData = (questionStore, completedMsg, notCompletedMsg) => {
    return questionStore[0].questionStore.map((question, index) => ({
        question: question.questionTitle,
        completion: !!question.answer ? completedMsg : notCompletedMsg,
        answer:
            question.questionType === 'date'
                ? formatDateLocale(formatDate(Number(question.answer)))
                : question.questionType === 'text' || question.questionType === 'singleChoice'
                ? question.answer
                : question.questionType === 'multiChoice' && question.answer
                ? question.answer.join('\r\n')
                : question.answer,
        question_type: question.questionType,
        attachments: question.answerAttachments,
        // attachments: [],
        id: index,
    }))
}

export const getStoreDetailsSurveyData = (surveyStore, completedMsg, notCompletedMsg, intl) => {
    return surveyStore[0].surveysStore.map((survey, index) => ({
        title: survey.title,
        completion: survey.answer ? completedMsg : notCompletedMsg,
        role:
            survey.assignment && survey.assignment.length
                ? intl.formatMessage({
                      id: survey.assignment[0],
                  })
                : null,
        // surveyUrl: survey.answer
        //     ? `${survey.surveyUrl}?Q_R=${survey.answer.surveyResponse.responseId}&readOnly=true`
        //     : notCompletedMsg,
        start_date: formatDateLocale(formatDate(survey.startDate)),
        end_date: formatDateLocale(formatDate(survey.endDate)),
        priority: intl.formatMessage({
            id: `modals.new.activity.section.scheduling.priority.${survey?.priority}`,
        }),
        description: survey.details,
        surveyUrl: `${survey.surveyUrl}?Q_R=${survey.answer?.surveyResponse?.responseId}&readOnly=true`,
        isCompleted: !!survey.answer,
        id: index,
    }))
}

export const getStatusGeneralData = (storeDetails: StoreProps[], projectType: string) => {
    let visited = 0
    storeDetails.forEach((store) => {
        if (store.confirmations) visited += 1
    })
    return [
        {
            type: projectType,
            percentage: Math.round((visited * 100) / storeDetails.length) + '%',
            completed_stores: `${visited}/${storeDetails.length}`,
        },
    ]
}

export const getStoreDetailsGeneralTableData = (
    storeDetails: StoreProps,
    questionMsg,
    taskMsg,
    surveyMsg,
    answerMsg
) => {
    let data = {}
    if (
        storeDetails.questionsStores &&
        storeDetails.questionsStores.length > 0 &&
        storeDetails.questionsStores[0].questionStore &&
        storeDetails.questionsStores[0].questionStore.length > 0
    ) {
        ;(data as any).questions = {
            type: questionMsg,
            completed: 0,
            total: storeDetails.questionsStores[0].questionStore.length,
        }
        storeDetails.questionsStores[0].questionStore.forEach((question) => {
            if (question.answer) (data as any).questions.completed += 1
        })
    }
    if (
        storeDetails.jobsStores &&
        storeDetails.jobsStores.length > 0 &&
        storeDetails.jobsStores[0].jobStore &&
        storeDetails.jobsStores[0].jobStore.length > 0
    ) {
        ;(data as any).tasks = {
            type: taskMsg,
            completed: 0,
            total: storeDetails.jobsStores[0].jobStore.length,
        }
        storeDetails.jobsStores[0].jobStore.forEach((task) => {
            if (
                task.questions &&
                task.questions.length > 0 &&
                task.questions.filter((question) => !question.answer).length === 0
            )
                (data as any).tasks.completed += 1
        })
    }
    if (
        storeDetails.surveysStores &&
        storeDetails.surveysStores.length > 0 &&
        storeDetails.surveysStores[0].surveysStore &&
        storeDetails.surveysStores[0].surveysStore.length > 0
    ) {
        ;(data as any).surveys = {
            type: surveyMsg,
            completed: 0,
            total: storeDetails.surveysStores[0].surveysStore.length,
        }
        storeDetails.surveysStores[0].surveysStore.forEach((survey) => {
            if (survey.answer) (data as any).surveys.completed += 1
        })
    }
    if (!storeDetails.questionsStores && !storeDetails.jobsStores && !storeDetails.surveysStores) {
        ;(data as any).answer = {
            type: answerMsg,
            completed: 0,
            total: [storeDetails.answer].length,
        }
        // [storeDetails.answer].forEach((answer) => {
        if (storeDetails.answer) (data as any).answer.completed += 1
        // })
    }
    return Object.keys(data).map((d: any) => ({
        type: data[d].type,
        completed_stores: `${data[d].completed}/${data[d].total}`,
        percentage: Math.round((data[d].completed * 100) / data[d].total) + '%',
    }))
}

export const getExecutionProjectSpecsDetail = (projectDetails, intl) => {
    let assignment = projectDetails?.assignment
        ?.filter((a, i) => projectDetails?.assignment?.indexOf(a) === i)
        .map((a: string) =>
            intl.formatMessage({
                id: `modals.filters.recipient.${a}`,
                defaultMessage: a,
            })
        )
    if (projectDetails.type === LayoutActivityType.SURVEY_TASK) {
        assignment = projectDetails.storesList[0].assignmentUserId
    }
    let defaultInfo = []
    if (!!projectDetails.badge) {
        defaultInfo = [
            {
                value: projectDetails.badge.activityPoints,
                label: 'modals.new.activity.points.assignment.points.title',
            },
            {
                value: projectDetails.badge.name,
                label: 'modals.news_execution.project_specs.details.badge',
            },
        ]
    }
    defaultInfo = defaultInfo.concat([
        {
            value: moment(projectDetails?.endDate).format('L'),
            label: 'modals.news_execution.project_specs.details.due_date',
        },
        {
            value: moment(projectDetails?.creatorUserInfo?.date).format('LT L'),
            label: 'modals.news_execution.project_specs.details.creation_time',
        },
        {
            value: projectDetails?.module,
            valueKey: `modals.new.activity.news.type.${projectDetails?.module}`,
            label: 'modals.news_execution.project_specs.details.module',
        },
        {
            value: projectDetails?.projectType,
            valueKey: `modals.new.activity.type.${projectDetails?.projectType}`,
            label: 'modals.news_execution.project_specs.details.project_type',
            disabled: projectDetails.type === LayoutActivityType.SURVEY_TASK,
        },
        {
            value: projectDetails?.type,
            valueKey: `modals.new.activity.type.${projectDetails?.type}`,
            label: 'modals.news_execution.project_specs.details.project_type',
            disabled: projectDetails.type !== LayoutActivityType.SURVEY_TASK,
        },
        {
            value: projectDetails?.childrenSubType,
            valueKey: `ph2.photographic_task.type.${projectDetails?.childrenSubType}`,
            label: 'ph2.photographic_task.type.detail',
            disabled: projectDetails?.type !== 'photographic',
        },
        {
            value: projectDetails?.department,
            valueKey: `modals.filters.department.${projectDetails?.department}`,
            label: 'modals.news_execution.project_specs.details.department',
            disabled: !projectDetails?.department,
        },
        {
            value: `${
                projectDetails?.creatorUserInfo?.nickname || projectDetails?.creatorUserInfo?.name
            }
            ${
                projectDetails?.creatorUserInfo?.nickname
                    ? ''
                    : ' ' + projectDetails?.creatorUserInfo?.lastname
            }`,
            label: 'modals.news_execution.project_specs.details.from',
        },
        {
            valueList: assignment,
            label: 'modals.news_execution.project_specs.details.assignment',
            isArray: true,
        },
        {
            value: projectDetails?.priority,
            valueKey: `modals.new.activity.section.scheduling.priority.${projectDetails?.priority}`,
            label: 'modals.news_execution.project_specs.details.priority',
        },
    ])
    if (canViewProjectDetailsExtraInfo(SmartlyActionItems.MENU)) {
        defaultInfo.splice(
            1,
            0,
            ...[
                {
                    value: projectDetails?.autoclose
                        ? `${Math.round(
                              (projectDetails?.autocloseDate - projectDetails?.endDate) /
                                  (1000 * 60 * 60 * 24)
                          )}`
                        : intl.formatMessage({
                              id: 'generic.no',
                          }),
                    label: 'modals.new.activity.section.scheduling.auto.close',
                },
                {
                    value: formatDateLocale(formatDate(projectDetails?.startDate)),
                    label: 'activity.table.label.start_date',
                },
            ]
        )
        defaultInfo = defaultInfo.concat([
            {
                value: projectDetails?.enableCommentsAndReactions
                    ? intl.formatMessage({
                          id: 'generic.yes',
                      })
                    : intl.formatMessage({
                          id: 'generic.no',
                      }),
                label: 'modals.new.activity.section.main.info.enable.comment.reaction',
            },
            {
                value: projectDetails?.enableMessaging
                    ? intl.formatMessage({
                          id: 'generic.yes',
                      })
                    : intl.formatMessage({
                          id: 'generic.no',
                      }),
                label: 'modals.new.activity.section.main.info.enable.messaging',
            },
        ])
    }
    return defaultInfo
}

export const getQuestionApi = (questionList) => {
    return questionList.map((question) => ({
        questionTitle: question.text,
        possibleAnswers: question.answerList.map((answer) => answer.value),
        questionType: question.type,
        attachmentsRequired: question.mandatoryAttachment,
        attachments: question.attachmentList?.map((attachment) => ({
            _id: attachment._id || attachment.id || attachment.file?.id || '',
        })),
        _id: question._id,
    }))
}

export const getQuestionFromDraftApi = (questionStore) => {
    return questionStore[0].questionStore.map((question) => ({
        questionTitle: question.questionTitle,
        questionType: question.questionType,
        attachmentsRequired: question.attachmentsRequired,
        possibleAnswers: question.possibleAnswers,
    }))
}

export const getTaskFromDraftApi = (taskStore) => {
    return taskStore[0].jobStore.map((task) => {
        return {
            title: task.title,
            details: task.details,
            questions: task.questions.map((question) => ({
                questionTitle: question.questionTitle,
                questionType: question.questionType,
                attachmentsRequired: question.attachmentsRequired,
                possibleAnswers: question.possibleAnswers,
            })),
            priority: task.priority ? 'urgent' : 'standard',
            startDate: new Date(task.startDate).getTime(),
            endDate: new Date(task.endDate).getTime(),
            assignment: task.assignment,
        }
    })
}

export const getSurveyFromDraftApi = (surveyStore) => {
    return surveyStore[0].surveysStore.map((survey) => ({
        title: survey.title,
        details: survey.details,
        priority: survey.priority ? 'urgent' : 'standard',
        startDate: new Date(survey.startDate).getTime(),
        endDate: new Date(survey.endDate).getTime(),
        recipient: survey.recipient,
        surveyId: survey.surveyId,
    }))
}

export const getEditSelectedStoreList = (
    selectedStoreList: StoreProps[],
    projectDetails: ProjectApiCreateActivityBody
) => {
    return selectedStoreList.map((store) => {
        const projectStore =
            projectDetails.storesList.find((s) => s.storeId === store.store_id) || store
        return {
            _id: projectDetails._id,
            status: projectStore.status || 'new',
            storeId: projectStore.storeId || projectStore.store_id,
            storeName: projectStore.storeName || projectStore.store_name,
        }
    })
}

export const getSaveAsDraftBody = (projectDetails) => {
    if (!projectDetails.recursiveInfo) delete projectDetails.recursiveInfo
    delete projectDetails.attachments
    delete projectDetails.rejectionUserInfo
    delete projectDetails.redoUserInfo
    projectDetails.storesList = projectDetails.storesList.map((store) => ({
        members: store.members,
        _id: store.projectId,
        status: store.status,
        storeId: store.storeId,
        storeName: store.storeName,
    }))
    projectDetails.questions = projectDetails.questions.map((question) => {
        let newQuestion = {
            ...question,
            projectId: projectDetails._id,
        }
        delete newQuestion.storeId
        return newQuestion
    })
    projectDetails.jobs = projectDetails.jobs.map((job) => {
        let newJob = {
            ...job,
            questions: job.questions.map((question) => {
                let newQuestion = {
                    ...question,
                    projectId: projectDetails._id,
                    jobId: job._id,
                }
                delete newQuestion.storeId
                delete newQuestion.answerInfo
                return newQuestion
            }),
        }
        delete newJob.projectId
        delete newJob.delegateUserInfo
        return newJob
    })
    if (projectDetails.cover)
        projectDetails.cover = {
            _id: projectDetails.cover._id,
        }
    delete projectDetails.editDate
    delete projectDetails.storesIdList
    delete projectDetails.visualizations
    delete projectDetails.favouriteUserIds
    delete projectDetails.confirmReading
    delete projectDetails.prevStatus
    delete projectDetails.closerUserInfo
    delete projectDetails.messageStores
    delete projectDetails.reasonChange
    delete projectDetails.reasonChoice
    return projectDetails
}

export const getEditProject = (form, projectDetails) => {
    delete projectDetails.attachments
    delete projectDetails.rejectionUserInfo
    delete projectDetails.redoUserInfo

    const newProjectDetails = {
        ...projectDetails,
        ...getActivityApi(form, projectDetails?.status, projectDetails?.projectType),
    }

    if (!Object.keys(newProjectDetails.smartFilter || {}).length) {
        delete newProjectDetails.smartFilter
    }

    if (!newProjectDetails.recursiveInfo) delete newProjectDetails.recursiveInfo

    if (
        newProjectDetails.smartFilter === null ||
        newProjectDetails.smartFilter === undefined ||
        Object.keys(newProjectDetails.smartFilter).length === 0
    ) {
        delete newProjectDetails.smartFilter
    }

    newProjectDetails.storesList = getEditSelectedStoreList(
        form.distribution.selectedStoreList.value,
        projectDetails
    )
    newProjectDetails.questions = newProjectDetails.questions.map((question) => {
        return {
            ...question,
            projectId: newProjectDetails._id,
        }
    })
    newProjectDetails.jobs = newProjectDetails.jobs.map((job) => {
        return {
            ...job,
            questions: job.questions.map((question) => {
                return {
                    ...question,
                    projectId: newProjectDetails._id,
                    jobId: job._id,
                }
            }),
        }
    })
    newProjectDetails.module = projectDetails.module
    delete newProjectDetails.editDate
    delete newProjectDetails.storesIdList
    delete newProjectDetails.visualizations
    delete newProjectDetails.favouriteUserIds
    delete newProjectDetails.confirmReading
    delete newProjectDetails.prevStatus
    delete newProjectDetails.closerUserInfo
    delete newProjectDetails.messageStores
    delete newProjectDetails.reasonChange
    delete newProjectDetails.reasonChoice
    delete newProjectDetails.assignmentUserIds
    delete newProjectDetails.canApproval
    delete newProjectDetails.totalStoreIdsCount
    delete newProjectDetails.badge
    if (newProjectDetails.creatorUserInfo) {
        delete newProjectDetails.creatorUserInfo.nickname
    }
    if (newProjectDetails.releaseUserInfo) {
        delete newProjectDetails.releaseUserInfo.nickname
    }
    if (projectDetails.module !== SmartlyActionItems.VISIT) {
        delete newProjectDetails.surveysSvt
        delete newProjectDetails.remoteVisit
    }
    return newProjectDetails
}

export const getActivityApi = (
    form,
    status: ProjectApiStatus,
    projectType: SmartlyActionItems
): ProjectApiCreateActivityBody => {
    form.questionList = form.questionList || []
    form.taskList = form.taskList || []
    form.surveyList = form.surveyList || []
    const startDate = form.scheduling.startDate.value
        ? new Date(form.scheduling.startDate.value)
        : null
    let endDate = form.scheduling.endDate?.value ? new Date(form.scheduling.endDate.value) : null

    let badgeProps = {}
    if (!!form[NewActivitySectionType.POINTS_ASSIGNMENT].badge.value?.id) {
        badgeProps = {
            badgeId: form[NewActivitySectionType.POINTS_ASSIGNMENT].badge.value.id,
            badgeType: projectType,
            badgeActivityPoints: form[NewActivitySectionType.POINTS_ASSIGNMENT].points.value,
        }
    }

    let body: ProjectApiCreateActivityBody = {
        projectTitle: form.mainInfo.title.value,

        assignment: form.mainInfo.recipient?.value ? form.mainInfo.recipient.value : [], // lista di ruoli assegnatari; per i progetti del communication l’array dovrà avere un solo elemento selezionabile tra regionalmanager, storemanager e storeassociate

        enableMessaging: form.mainInfo.messagingEnabled?.value || false, // flag per abilitare lo scambio di messaggi sul progetto
        enableCommentsAndReactions: form.mainInfo.commentReaction?.value || false,
        priority: form.scheduling.priority?.value, //priorità del progetto. I valori possibili sono standard e urgent

        status, // utilizzato per differenziare il salvataggio in draft (valorizzare il campo con draft) dal salvataggio effettivo (valorizzare il campo con toBeReleased)

        projectType, // tipo di progetto. I valori possibili sono task, news e visit

        module: form.subType?.type?.label, // modulo al quale appartiene il progetto. I valori possibili sono communication, svt e pm

        type: form.subType?.type?.value, // modulo al quale appartiene il progetto. I valori possibili sono communication, svt e pm
        childrenSubType: form.subType?.photoType,

        requiredConfirmReading:
            !!form.mainInfo.confirmRead?.value &&
            form.subType?.type?.value !== SmartlyActionSubtypes.PICTURE_MGMT, // flag per indicare che per l’esecuzione del task è necessario aver confermato la lettura

        information: form.mainInfo.info.value || '', // campo contenente la descrizione del progetto

        //lista degli storeId associati al progetto, in formato stringa
        storesIdList: form.distribution?.selectedStoreList?.value.map(
            (store) => store.store_id || store
        ),
        approverRequired: form.distribution?.approvalRequired || false,
        approvers: form.distribution?.approversList,

        startDate: startDate ? startDate.getTime() : null, //data di inizio progetto. Il formato della data è un long (e.g. 1670761800000)

        endDate: endDate ? endDate.setHours(23, 59, 59) : null, // data di fine progetto. Il formato della data è un long (e.g. 1670761800000)

        visibility: form.scheduling.projectVisibility?.value, // informazione sulla visibilità del progetto. Può avere i seguenti valori:

        smartFilter: form.smartFilter,

        autoclose: form.scheduling.autoClose?.value || false, //se true il progetto si chiuderà automaticamente alla endDate
        autocloseDate:
            endDate && form.scheduling.autoCloseDays?.value > 0
                ? endDate.setDate(endDate.getDate() + form.scheduling.autoCloseDays?.value)
                : endDate
                ? endDate.getTime()
                : null,
        visibilityCalendar: form.scheduling.calendarVisibility.value, // se true il progetto sarà visibile anche nella sezione del calendario
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,

        numberOfPhotos: form.action?.numberOfPhotos,
        questions: getQuestionApi(form.questionList),
        jobs: form.taskList?.map((task) => ({
            title: task.title,
            details: task.description,
            questions: getQuestionApi(task.questionList),
            priority: task.urgent ? 'urgent' : 'standard',
            startDate: task.startDate
                ? new Date(task.startDate).getTime() < startDate.getTime()
                    ? startDate.getTime()
                    : new Date(task.startDate).getTime()
                : null,
            endDate: task.endDate ? new Date(task.endDate).setHours(23, 59, 59) : null,
            assignment: task.recipient,
            attachments: task.attachmentList?.map((attachment) => ({
                _id: attachment._id || attachment.id || attachment.file?.id || '',
            })),
            _id: task._id,
        })),
        surveys: form.surveyList?.map((survey) => ({
            title: survey.title,
            details: survey.description,
            priority: survey.urgent ? 'urgent' : 'standard',
            startDate: survey.startDate
                ? new Date(survey.startDate).getTime() < startDate.getTime()
                    ? startDate.getTime()
                    : new Date(survey.startDate).getTime()
                : null,
            endDate: survey.endDate ? new Date(survey.endDate).setHours(23, 59, 59) : null,
            assignment: survey.recipient,
            surveyId: survey.selectedSurveyList.surveyId,
            attachments: survey.attachmentList?.map((attachment) => ({
                _id: attachment._id || attachment.id || attachment.file?.id || '',
            })),
            _id: survey._id,
        })),
        isRecursive: !!form.scheduling.recurring?.value,
        ...badgeProps,
    }
    if (form.scheduling.recurring?.value) {
        body.recursiveInfo = {
            recursiveDateTimeFrom: form.scheduling.recurringStartDate.value
                ? new Date(form.scheduling.recurringStartDate.value).getTime()
                : null,
            noDeadline: form.scheduling.recurringUntil.value === 'endless',
        }
        body.recursiveInfo[form.scheduling.recurringType.value] = {
            timeInterval: form.scheduling.recurringEvery.value,
        }

        if (form.scheduling.recurringType.value === 'weekly')
            body.recursiveInfo[form.scheduling.recurringType.value].day =
                form.scheduling.recurringOn.value
        if (form.scheduling.recurringUntil.value === 'after')
            body.recursiveInfo.endOccurrences = form.scheduling.recurringOccurrency.value
        if (form.scheduling.recurringUntil.value === 'until')
            body.recursiveInfo.recursiveDateTimeTo = form.scheduling.recurringEndDate.value
                ? new Date(form.scheduling.recurringEndDate.value).getTime()
                : null
    }

    if (form.scheduling.projectVisibility?.value === 'immediate')
        body.visibilityDate = new Date().getTime()
    if (form.scheduling.projectVisibility?.value === 'overnight')
        body.visibilityDate = new Date(startDate.setDate(startDate.getDate() + 1)).setHours(0, 0, 0)
    if (form.scheduling.projectVisibility?.value === 'daysBeforeStartDate')
        body.visibilityDate = new Date(
            startDate.setDate(startDate.getDate() - form.scheduling.daysBefore.value)
        ).getTime()
    if (form.scheduling.projectVisibility?.value === 'specificDate')
        body.visibilityDate = new Date(form.scheduling.specificDate.value).getTime()
    if (form.mainInfo.cover) body.cover = { _id: form.mainInfo.cover?.value.id }
    if (form.mainInfo.attachmentList.value?.length) {
        body.attachments = []
        form.mainInfo.attachmentList.value.forEach((attachment) => {
            if (attachment)
                body.attachments.push({
                    _id: attachment._id || attachment.id || attachment.file?.id || '',
                })
        })
    }

    if (!!form.smartFilter && Object.keys(form.smartFilter).length > 0) {
        body.smartFilter = form.smartFilter
    }
    if (form.mainInfo.department?.value) {
        body.department = form.mainInfo.department.value
    }
    return body
}

export const getDateDiff = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1)
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

export const digestFormDates = (form: ProjectApiCreateActivityBody) => {
    let _form = { ...form }

    const subtasks = ['jobs', 'surveys']
    subtasks.forEach((st) => {
        if (_form[st]?.length)
            _form[st] = _form[st].map((s) => ({
                ...s,
                startDate: s.startDate < _form.startDate ? _form.startDate : s.startDate,
            }))
    })

    return _form
}
